'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import type { RsBillingCycle, ServiceOfferingFieldsFragment, ServiceTierFieldsFragment } from '@/modules/__generated__/graphql/switchboard-generated';
import { clearServicePurchasePersistedState, hasReachedConfirmationStep } from '../lib/persistence-utils';
import { createServicePurchaseStore } from '../stores/service-purchase-store';
import type { PurchaseOptionGroup, PurchaseTotals, ServicePurchaseActions, ServicePurchaseState, ServicePurchaseStep } from '../types';
export const ServicePurchaseStoreContext = createContext<ReturnType<typeof createServicePurchaseStore> | null>(null);
export interface ServicePurchaseStoreProviderProps {
  children: React.ReactNode;
  services: ServiceOfferingFieldsFragment;
}
function ServicePurchaseStoreProvider(t0) {
  const $ = _c(10);
  const {
    children,
    services
  } = t0;
  let t1;
  if ($[0] !== services) {
    t1 = () => createServicePurchaseStore({
      services
    });
    $[0] = services;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [store] = useState(t1);
  let t2;
  if ($[2] !== store) {
    t2 = () => {
      const {
        visitedSteps,
        disabledSteps,
        services: currentServices
      } = store.getState();
      if (!hasReachedConfirmationStep({
        visitedSteps,
        disabledSteps
      })) {
        return;
      }
      store.setState(store.getInitialState(), true);
      clearServicePurchasePersistedState(currentServices.id);
    };
    $[2] = store;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const resetCompletedPurchaseState = t2;
  let t3;
  let t4;
  if ($[4] !== resetCompletedPurchaseState) {
    t3 = () => () => {
      resetCompletedPurchaseState();
    };
    t4 = [resetCompletedPurchaseState];
    $[4] = resetCompletedPurchaseState;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== children || $[8] !== store) {
    t5 = <ServicePurchaseStoreContext.Provider value={store}>{children}</ServicePurchaseStoreContext.Provider>;
    $[7] = children;
    $[8] = store;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
function useServicePurchaseStoreContext() {
  const store = useContext(ServicePurchaseStoreContext);
  if (!store) {
    throw new Error("useServicePurchaseStoreContext must be used within a ServicePurchaseStoreProvider");
  }
  return store;
}
function useServicePurchaseState() {
  const store = useServicePurchaseStoreContext();
  return useStore(store, useShallow(_temp));
}
function _temp(state) {
  const {
    actions: _,
    ...rest
  } = state;
  return rest;
}
function useServicePurchaseActions() {
  const store = useServicePurchaseStoreContext();
  return useStore(store, _temp2);
}
function _temp2(state) {
  return state.actions;
}
function useSelectedTier() {
  const store = useServicePurchaseStoreContext();
  return useStore(store, _temp3);
}
function _temp3(state) {
  return state.selectedTier;
}
function useSelectedBillingCycle() {
  const store = useServicePurchaseStoreContext();
  return useStore(store, _temp4);
}
function _temp4(state) {
  return state.selectedBillingCycle;
}
function useComputedTiers() {
  const store = useServicePurchaseStoreContext();
  return useStore(store, _temp5);
}
function _temp5(state) {
  return state.tiers;
}
function useOptionGroups() {
  const store = useServicePurchaseStoreContext();
  return useStore(store, useShallow(_temp7));
}
function _temp7(state) {
  return state.optionGroups.filter(_temp6);
}
function _temp6(g) {
  return !g.isAddOn;
}
function useSelectedAddOns() {
  const store = useServicePurchaseStoreContext();
  return useStore(store, useShallow(_temp9));
}
function _temp9(state) {
  return state.optionGroups.filter(_temp8);
}
function _temp8(g) {
  return g.isAddOn && g.isSelected;
}
function useAllOptionGroups() {
  const store = useServicePurchaseStoreContext();
  return useStore(store, useShallow(_temp10));
}
function _temp10(state) {
  return state.optionGroups;
}
function usePurchaseTotals() {
  const store = useServicePurchaseStoreContext();
  return useStore(store, _temp11);
}
function _temp11(state) {
  return state.totals;
}
function useServiceOffering() {
  const store = useServicePurchaseStoreContext();
  return useStore(store, _temp12);
}
function _temp12(state) {
  return state.services;
}
function useHoveredTier() {
  const store = useServicePurchaseStoreContext();
  return useStore(store, _temp13);
}
function _temp13(state) {
  return state.hoveredTierId;
}
function useFacets() {
  const store = useServicePurchaseStoreContext();
  return useStore(store, _temp14);
}
function _temp14(state) {
  return state.facets;
}
function useServicePurchaseStep() {
  const $ = _c(15);
  const store = useServicePurchaseStoreContext();
  const activeStep = useStore(store, _temp15);
  const visitedSteps = useStore(store, useShallow(_temp16));
  const disabledSteps = useStore(store, useShallow(_temp17));
  const actions = useStore(store, _temp18);
  let t0;
  if ($[0] !== visitedSteps) {
    t0 = new Set(visitedSteps);
    $[0] = visitedSteps;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const visitedSet = t0;
  let t1;
  if ($[2] !== disabledSteps) {
    t1 = new Set(disabledSteps);
    $[2] = disabledSteps;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const disabledSet = t1;
  let t2;
  if ($[4] !== visitedSet) {
    t2 = step => visitedSet.has(step);
    $[4] = visitedSet;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const hasVisitedStep = t2;
  let t3;
  if ($[6] !== disabledSet) {
    t3 = step_0 => disabledSet.has(step_0);
    $[6] = disabledSet;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const isStepDisabled = t3;
  let t4;
  if ($[8] !== actions.goBack || $[9] !== actions.goToStep || $[10] !== actions.resetPostConfigureSteps || $[11] !== activeStep || $[12] !== hasVisitedStep || $[13] !== isStepDisabled) {
    t4 = {
      activeStep,
      hasVisitedStep,
      isStepDisabled,
      goToStep: actions.goToStep,
      goBack: actions.goBack,
      resetPostConfigureSteps: actions.resetPostConfigureSteps
    };
    $[8] = actions.goBack;
    $[9] = actions.goToStep;
    $[10] = actions.resetPostConfigureSteps;
    $[11] = activeStep;
    $[12] = hasVisitedStep;
    $[13] = isStepDisabled;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}
function _temp18(state_2) {
  return state_2.actions;
}
function _temp17(state_1) {
  return state_1.disabledSteps;
}
function _temp16(state_0) {
  return state_0.visitedSteps;
}
function _temp15(state) {
  return state.activeStep;
}
export { ServicePurchaseStoreProvider, useFacets, useHoveredTier, useServicePurchaseState, useServicePurchaseActions, useSelectedTier, useSelectedBillingCycle, useComputedTiers, useOptionGroups, useSelectedAddOns, useAllOptionGroups, usePurchaseTotals, useServiceOffering, useServicePurchaseStep };